/**
 * ── EntitlementGate ──
 * Unified access gate component that replaces scattered FeatureGate/ModuleGate usage.
 * Supports all commercial access states: included, trial, bonus, addon, locked, hidden.
 *
 * Usage:
 *   <EntitlementGate module="signature_events">
 *     <SignatureEventsPage />
 *   </EntitlementGate>
 *
 *   <EntitlementGate capability="events.surveys" tease>
 *     <SurveyBuilder />
 *   </EntitlementGate>
 */

import { motion } from "framer-motion";
import { ArrowUpRight, Clock, Gift, Lock, ShieldAlert, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { useEntitlementAccess, type EntitlementResult } from "@/hooks/useEntitlementAccess";
import { useIsAdmin } from "@/hooks/useUserRole";
// commercial-catalog types used by EntitlementResult from useEntitlementAccess

interface EntitlementGateProps {
  /** Module key to check (e.g., "events", "sponsors") */
  module?: string;
  /** Capability key to check (e.g., "events.surveys") */
  capability?: string;
  children: React.ReactNode;
  /** Custom label for locked state */
  label?: string;
  /** If true, renders nothing instead of a locked card */
  hideWhenLocked?: boolean;
  /** If true, shows a teaser/upsell card instead of a lock for addon state */
  tease?: boolean;
  /** Custom fallback to render when locked/teased */
  fallback?: React.ReactNode;
}

function TrialBanner({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 mb-3 rounded-lg border border-primary/20 bg-primary/5 text-xs">
      <Clock className="w-3.5 h-3.5 text-primary" />
      <span className="text-foreground font-medium">{label}</span>
      <Badge variant="secondary" className="text-[9px] ml-auto">Trial</Badge>
    </div>
  );
}

function BonusBanner({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 mb-3 rounded-lg border border-accent/30 bg-accent/10 text-xs">
      <Gift className="w-3.5 h-3.5 text-accent-foreground" />
      <span className="text-foreground font-medium">{label}</span>
      <Badge variant="outline" className="text-[9px] ml-auto">Bonus</Badge>
    </div>
  );
}

function LockedState({ entitlement, label }: { entitlement: EntitlementResult; label: string }) {
  const { restrictedBy, accessState } = entitlement;

  const icon = restrictedBy === "platform" || accessState === "coming_soon"
    ? <ShieldAlert className="w-5 h-5 text-muted-foreground" />
    : <Lock className="w-5 h-5 text-muted-foreground" />;

  const message = accessState === "coming_soon"
    ? "This feature is not yet available on the platform."
    : restrictedBy === "association"
      ? "This feature is restricted by your association. Contact your association administrator for access."
      : "This feature is not activated for your organization.";

  const ctaLabel = accessState === "coming_soon"
    ? "Coming Soon"
    : restrictedBy === "association"
      ? "Contact Association Admin"
      : "Contact Admin";

  const handleCta = () => {
    if (accessState === "coming_soon") {
      toast.info(`${label} is coming soon.`);
    } else {
      toast.info(`Contact your administrator to enable ${label}.`);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center py-16 px-6 text-center"
    >
      <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="text-sm font-semibold text-foreground mb-1">{label} — Not Available</h3>
      <p className="text-[11px] text-muted-foreground max-w-sm mb-4">{message}</p>
      <button
        type="button"
        onClick={handleCta}
        className="flex items-center gap-1.5 px-4 py-2 text-[11px] font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
      >
        <ArrowUpRight className="w-3 h-3" />
        {ctaLabel}
      </button>
    </motion.div>
  );
}

function TeaseState({ entitlement, label }: { entitlement: EntitlementResult; label: string }) {
  const addOn = entitlement.availableAddOns[0];
  const planName = entitlement.minPlan?.name;

  const handleUpgrade = () => {
    if (addOn) {
      toast.info(`${addOn.name} is available as an add-on (${addOn.price}). Contact your administrator.`);
    } else if (planName) {
      toast.info(`Upgrade to the ${planName} plan to unlock ${label}.`);
    } else {
      toast.info(`Contact your administrator to unlock ${label}.`);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center py-16 px-6 text-center"
    >
      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
        <Sparkles className="w-5 h-5 text-primary" />
      </div>
      <h3 className="text-sm font-semibold text-foreground mb-1">Unlock {label}</h3>
      {addOn ? (
        <div className="max-w-sm mb-4">
          <p className="text-[11px] text-muted-foreground mb-2">{addOn.description}</p>
          <p className="text-xs font-medium text-primary">{addOn.price}</p>
        </div>
      ) : planName ? (
        <p className="text-[11px] text-muted-foreground max-w-sm mb-4">
          This feature is available on the <span className="text-primary font-medium">{planName}</span> plan and above.
        </p>
      ) : (
        <p className="text-[11px] text-muted-foreground max-w-sm mb-4">
          This feature requires an upgrade. Contact your administrator.
        </p>
      )}
      <button
        type="button"
        onClick={handleUpgrade}
        className="flex items-center gap-1.5 px-4 py-2 text-[11px] font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
      >
        <ArrowUpRight className="w-3 h-3" />
        {addOn ? `Add ${addOn.name}` : `Upgrade to ${planName || "Premium"}`}
      </button>
    </motion.div>
  );
}

const EntitlementGate = ({
  module,
  capability,
  children,
  label,
  hideWhenLocked,
  tease,
  fallback,
}: EntitlementGateProps) => {
  const { check } = useEntitlementAccess();
  const { isMasterAdmin } = useIsAdmin();

  const key = capability || module;
  if (!key) return <>{children}</>;

  // Platform admins always have access
  if (isMasterAdmin) return <>{children}</>;

  const entitlement = check(key);
  const displayLabel = label || key.replace(/[._]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  // Fully accessible states
  if (entitlement.hasAccess) {
    return (
      <>
        {entitlement.accessState === "trial" && <TrialBanner label={`${displayLabel} — Trial Access`} />}
        {entitlement.accessState === "bonus" && <BonusBanner label={`${displayLabel} — Bonus Access`} />}
        {children}
      </>
    );
  }

  // Hidden
  if (entitlement.behavior === "hide" || hideWhenLocked) return null;

  // Custom fallback
  if (fallback) return <>{fallback}</>;

  // Teased/upsellable
  if (entitlement.behavior === "tease" || tease) {
    return <TeaseState entitlement={entitlement} label={displayLabel} />;
  }

  // Locked
  return <LockedState entitlement={entitlement} label={displayLabel} />;
};

export default EntitlementGate;
